import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import "./Result.css";
import ResultModalLayout from '@/components/ResultModalLayout';

// Helper function to map card string to image path
const getCardImage = (cardString) => {
  if (!cardString) return null;
  const match = cardString.match(/^([A-Z0-9]+)([A-Z])\2?$/);
  if (match) {
    const value = match[1];
    const suit = match[2];
    const cleanCard = `${value}${suit}`;
    return `/assets/img/cards/${cleanCard}.png`;
  }
  return `/assets/img/cards/${cardString}.png`;
};

// Parse 9 cards: Player A (2 cards), Player B (2 cards), Board (5 cards)
const parseCards = (cardString) => {
  if (!cardString) return { playerA: [], playerB: [], board: [] };
  const cards = cardString.split(',').map(c => c.trim());
  
  return {
    playerA: cards.slice(0, 2).map(c => getCardImage(c)),
    playerB: cards.slice(2, 4).map(c => getCardImage(c)),
    board: cards.slice(4, 9).map(c => getCardImage(c)),
  };
};

// Parse rdesc: "Player B#A : Two Pair  |  B : Three of a Kind"
const parseRdesc = (rdesc) => {
  if (!rdesc) return { winner: "", playerA: "", playerB: "" };
  const parts = rdesc.split('#');
  
  const winner = parts[0] || ""; // "Player B"
  const other = parts[1] || ""; // "A : Two Pair  |  B : Three of a Kind"
  
  // Parse player A and B from other string
  let playerA = "";
  let playerB = "";
  if (other) {
    const playerParts = other.split('|').map(p => p.trim());
    playerParts.forEach(part => {
      if (part.startsWith('A :')) {
        playerA = part.replace('A :', '').trim();
      } else if (part.startsWith('B :')) {
        playerB = part.replace('B :', '').trim();
      }
    });
  }
  
  return { winner, other, playerA, playerB };
};

const ResultPoker20 = ({ 
  data = [], 
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('poker20');
        if (response?.data?.data?.res) {
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };
    
    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleResultClick = useCallback(async (result, index) => {
    setSelectedResult(result);
    setLoading(true);
    
    if (lastResults.length === 0) {
      try {
        const response = await getLastResults('poker20');
        if (response?.data?.data?.res) {
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    }
    
    let mid = null;
    if (index < lastResults.length) {
      mid = lastResults[index]?.mid;
    } else if (index < data.length + (gameData?.lrs?.length || 0)) {
      mid = gameData?.data?.data?.mid || gameData?.mid;
    }
    
    if (mid && detailResult?.rid !== mid.toString()) {
      try {
        const response = await getDetailResults('poker20', mid.toString());
        if (response?.success && response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        } else if (response?.success && response?.data?.t1) {
          setDetailResult(response.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }
    
    setLoading(false);
    setShowModal(true);
  }, [lastResults, data, gameData, detailResult]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  const getModalData = useMemo(() => {
    if (!selectedResult || !showModal) return null;

    const cards = detailResult?.card ? parseCards(detailResult.card) : { playerA: [], playerB: [], board: [] };
    const rdescData = detailResult?.rdesc ? parseRdesc(detailResult.rdesc) : {};
    
    const winner = detailResult?.winnat || rdescData.winner || "";
    const winValue = detailResult?.win;
    const isPlayerAWinner = winValue === "1" || winner === "Player A" || selectedResult === "A";
    const isPlayerBWinner = winValue === "2" || winner === "Player B" || selectedResult === "B";

    return {
      roundId: detailResult?.rid || gameData?.data?.data?.mid || gameData?.data?.mid || gameData?.mid || "",
      matchTime: detailResult?.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      playerA: {
        cards: cards.playerA,
        isWinner: isPlayerAWinner,
      },
      playerB: {
        cards: cards.playerB,
        isWinner: isPlayerBWinner,
      },
      board: cards.board,
      resultDetails: {
        winner: winner,
        other: rdescData.other || "",
        playerA: rdescData.playerA || "",
        playerB: rdescData.playerB || "",
      },
    };
  }, [selectedResult, showModal, detailResult, gameData]);

  const modalData = getModalData;
  const { winner = "", other = "", playerA: playerAHand = "", playerB: playerBHand = "" } = modalData?.resultDetails || {};

  const allResults = lastResults.length > 0
    ? lastResults.map(r => r.win)
    : [...data, ...(gameData?.lrs || []), ...(gameData?.data?.lrs || [])];

  return (
    <>
      <div className="result-row">
        {allResults.map((res, index) => (
          <div
            key={index}
            className={`result-circle ${res === "A" ? "yes" : "no"}`}
            onClick={() => handleResultClick(res, index)}
            style={{ cursor: 'pointer' }}
          >
            {res}
          </div>
        ))}
      </div>

      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="20-20 Poker Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12">
                <h6 className="text-right">
                  <b>Round Id :</b> {modalData.roundId}
                </h6>
              </div>
            </div>

            {/* Player Cards Section - Flex Row */}
            <div className="row player-container m-t-10">
              <div className="col-md-12">
                <div className="player-image-container row" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '10px' }}>
                  {/* Player A */}
                  <div className="text-center player-number" style={{ flex: '1' }}>
                    <h4>Player A</h4>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '5px' }}>
                      {modalData.playerA?.cards?.map((card, idx) => (
                        card && (
                          <img
                            key={idx}
                            src={card}
                            alt={`Player A Card ${idx + 1}`}
                            onError={(e) => {
                              const cardName = card.split('/').pop();
                              const fallbackPath = `/assets/img/cards/${cardName}`;
                              if (e.target.src !== fallbackPath) {
                                e.target.src = fallbackPath;
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        )
                      ))}
                    </div>
                    <div className="col-md-12 text-center">
                      {modalData.playerA?.isWinner ? (
                        <label className="winner-label bg-success m-t-20">
                          <i className="fas fa-trophy m-r-5"></i> Winner
                        </label>
                      ) : (
                        <label className="m-t-20">&nbsp;</label>
                      )}
                    </div>
                  </div>

                  {/* Player B */}
                  <div className="text-center player-number" style={{ flex: '1' }}>
                    <h4>Player B</h4>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '5px' }}>
                      {modalData.playerB?.cards?.map((card, idx) => (
                        card && (
                          <img
                            key={idx}
                            src={card}
                            alt={`Player B Card ${idx + 1}`}
                            onError={(e) => {
                              const cardName = card.split('/').pop();
                              const fallbackPath = `/assets/img/cards/${cardName}`;
                              if (e.target.src !== fallbackPath) {
                                e.target.src = fallbackPath;
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        )
                      ))}
                    </div>
                    <div className="col-md-12 text-center">
                      {modalData.playerB?.isWinner ? (
                        <label className="winner-label bg-success m-t-20">
                          <i className="fas fa-trophy m-r-5"></i> Winner
                        </label>
                      ) : (
                        <label className="m-t-20">&nbsp;</label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Table Cards */}
            <div className="row m-t-10 text-center">
              <div className="col-md-12 player-number">
                <div className="player-image-container">
                  {modalData.board?.map((card, idx) => (
                    card && (
                      <img
                        key={idx}
                        src={card}
                        alt={`Table Card ${idx + 1}`}
                        onError={(e) => {
                          const cardName = card.split('/').pop();
                          const fallbackPath = `/assets/img/cards/${cardName}`;
                          if (e.target.src !== fallbackPath) {
                            e.target.src = fallbackPath;
                          } else {
                            e.target.style.display = 'none';
                          }
                        }}
                      />
                    )
                  ))}
                </div>
              </div>
            </div>

            {/* Result Details */}
            <div className="row board-result m-t-10">
              <div className="col-md-12">
                <div className="board-result-inner">
                  {winner && (
                    <div className="m-b-5">
                      <span className="greenbx">WINNER:</span> {winner}
                    </div>
                  )}
                  {playerAHand && (
                    <div className="m-b-5">
                      <span className="redbx">PLAYER A:</span> A : {playerAHand}
                    </div>
                  )}
                  {playerBHand && (
                    <div className="m-b-5">
                      <span className="yellowbx">PLAYER B:</span> B : {playerBHand}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultPoker20;

